import { Link } from "react-router-dom";
import { ArrowRight, Hammer } from "lucide-react";
import { useSEO, BASE } from "@/lib/seo";
import { METIERS } from "@/lib/partenaires";
import ArticleShell, { P } from "@/components/site/ArticleShell";
import { Reveal } from "@/components/site/Reveal";

export default function PartenairesPage() {
  useSEO(
    "Annuaire des artisans et ateliers d'art | Sculpteur Français",
    "Ferronnerie d'art, fabrication résine : découvrez les artisans partenaires qui participent à la fabrication des sculptures, leurs ateliers et leurs savoir-faire.",
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Annuaire des artisans et ateliers d'art",
      inLanguage: "fr-FR",
      url: `${BASE}/partenaires-metiers-d-art`,
      isPartOf: { "@type": "WebSite", name: "Sculpteur Français", url: `${BASE}` },
    },
    {
      type: "article",
      ogTitle: "Annuaire des artisans et ateliers d'art",
      ogDescription:
        "Derrière chaque sculpture, des artisans d'exception : découvrez nos partenaires par métier — ferronnerie, résine — et leurs réalisations.",
      breadcrumb: [{ name: "Partenaires", path: "/partenaires-metiers-d-art" }],
    }
  );

  return (
    <ArticleShell
      testId="partenaires-page"
      eyebrow="Artisans & savoir-faire"
      titleLines={["L'annuaire des artisans", "et ateliers d'art de la sculpture"]}
      lead="Derrière chaque sculpture, il y a des artisans qui maîtrisent des savoir-faire rares : forger le métal, mouler la résine, parfaire une finition. Cette page les met en lumière, métier par métier — et leur renvoie le trafic qu'ils méritent."
    >
      <Reveal className="mt-10">
        <p
          data-testid="partenaires-inscription-gratuite"
          className="border border-bronze/40 bg-bronze/5 px-6 py-5 text-base font-light leading-relaxed text-body sm:text-lg"
        >
          Vous êtes artisan ou atelier d'art ? Céramistes et potiers sont les bienvenus :{" "}
          <Link
            to="/devenez-partenaire-metiers-d-art"
            className="link-underline font-normal text-bronze transition-colors hover:text-ink"
          >
            L'inscription est gratuite.
          </Link>
        </p>
      </Reveal>

      <div className="mt-14 space-y-6">
        {METIERS.map((m, i) => (
          <Reveal key={m.slug} delay={0.08 * i}>
            <Link
              to={`/partenaires/${m.slug}`}
              data-testid={`partenaires-metier-${m.slug}`}
              className="group flex items-center justify-between border border-ink/15 p-8 transition-colors duration-300 hover:border-bronze"
            >
              <div>
                <p className="eyebrow flex items-center gap-2">
                  <Hammer className="h-3.5 w-3.5 text-bronze" />
                  {m.name}
                </p>
                <p className="mt-3 font-serif text-2xl font-normal text-ink transition-colors group-hover:text-bronze sm:text-3xl">
                  {m.title}
                </p>
                <p className="mt-2 text-sm font-light text-smoke">
                  {m.partners.length} partenaire{m.partners.length > 1 ? "s" : ""} — {m.partners.map((p) => p.contact.company).join(", ")}
                </p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-bronze transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14">
        <div className="border border-ink/15 p-8" data-testid="partenaires-prospection-card">
          <p className="eyebrow">Vous êtes artisan des métiers d'art ?</p>
          <p className="mt-4 text-base font-light leading-relaxed text-body sm:text-lg">
            Fonderie, taille de pierre, chromage, moulage… Rejoignez gratuitement l'annuaire des
            métiers d'art de la sculpture et présentez votre savoir-faire sur ce site.
          </p>
          <Link
            to="/devenez-partenaire-metiers-d-art"
            data-testid="partenaires-devenir-link"
            className="group mt-6 inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze"
          >
            Inscription gratuite à l'annuaire
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>

      <P>
        Cette section s'enrichira au fil des collaborations : de nouveaux artisans rejoindront les
        métiers déjà représentés, et de nouvelles familles de savoir-faire — fonderie, taille de
        pierre, chromage — viendront compléter l'annuaire.
      </P>
    </ArticleShell>
  );
}
